import { useEffect, useRef, useState, useCallback } from 'react';
import { SocialType, SocialAuthResult, SocialAuthCode, socialAuthStorageKey } from 'constant/socialAuth';
import storage from 'utils/storage';
import user from 'stores/user';

const twitterClientId = process.env.REACT_APP_TWITTER_CLIENT_ID;
const redirectUrl = process.env.REACT_APP_TWITTER_REDIRECT_URL!;

const genTwitterAuthUrl = () => {
    const data: any = {
        walletAddress: user.address,
    };
    if (process.env.NODE_ENV !== 'production') {
        data.target = 'local';
    }
    const state = btoa(JSON.stringify(data));
    const url = `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=${twitterClientId}&redirect_uri=${encodeURIComponent(
        redirectUrl,
    )}&state=${state}&scope=tweet.read%20users.read%20follows.read%20offline.access%20like.read&code_challenge=code&code_challenge_method=plain`;

    return url;
};

const discordClientId = process.env.REACT_APP_DISCORD_CLIENT_ID!;
const discordRedirectUrl = process.env.REACT_APP_DISCORD_REDIRECT_URL!;

const genDiscordAuthUrl = () => {
    const data: any = {
        walletAddress: user.address,
    };
    if (process.env.NODE_ENV !== 'production') {
        data.target = 'local';
    }
    const state = btoa(JSON.stringify(data));
    const url = `https://discord.com/oauth2/authorize?response_type=code&client_id=${discordClientId}&scope=identify%20guilds.join&state=${state}&redirect_uri=${encodeURIComponent(
        discordRedirectUrl,
    )}&prompt=consent&integration_type=0`;
    return url;
};

const openAuthWindow = (url: string, name?: string) => {
    const width = 400;
    const height = 600;
    const top = Math.max((window.screen.availHeight - height) / 2, 0);
    const left = Math.max((window.screen.availWidth - width) / 2, 0);
    return window.open(url, name || 'auth_window', `width=${width},height=${height},top=${top},left=${left}`);
};

const useSocialAuth = (): [
    (type: SocialType) => Promise<SocialAuthResult>,
    (type: SocialType) => Promise<SocialAuthCode>,
    boolean,
    SocialAuthResult | undefined,
] => {
    const [pending, setPending] = useState<boolean>(false);
    const [authResult, setAuthResult] = useState<SocialAuthResult>();
    const authWindowRef = useRef<Window | null>(null);
    const resolveRef = useRef<(authResult: SocialAuthResult) => void>();
    const rejectRef = useRef<(reason?: any) => void>();
    const authTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const cleanupAuth = useCallback(() => {
        if (authTimeoutRef.current) {
            clearTimeout(authTimeoutRef.current);
        }
        resolveRef.current = undefined;
        rejectRef.current = undefined;
        // Clean up both Twitter and Discord storage
        storage.remove(socialAuthStorageKey(SocialType.Twitter));
        storage.remove(socialAuthStorageKey(SocialType.Discord));
    }, []);

    const auth = useCallback(
        (type: SocialType): Promise<any> => {
            cleanupAuth();
            return new Promise((resolve, reject) => {
                resolveRef.current = resolve;
                rejectRef.current = reject;
                try {
                    let url = '';
                    if (type === SocialType.Twitter) {
                        url = genTwitterAuthUrl();
                    } else if (type === SocialType.Discord) {
                        url = genDiscordAuthUrl();
                    } else {
                        reject(new Error('coming soon'));
                        return;
                    }
                    authWindowRef.current = openAuthWindow(url);
                    if (!authWindowRef.current) {
                        throw new Error('New window is rejected by the browser, please try again.');
                    }

                    setPending(true);

                    // timeout check
                    authTimeoutRef.current = setTimeout(() => {
                        if (authWindowRef.current && !authWindowRef.current.closed) {
                            authWindowRef.current.close();
                        }
                        reject(new Error('Authorization process timed out.'));
                        cleanupAuth();
                    }, 300000); // 5 mins

                    // auth window closed check
                    const checkWindowClosed = setInterval(() => {
                        if (authWindowRef.current?.closed) {
                            clearInterval(checkWindowClosed);
                            if (!authResult?.code) {
                                // user close auth window before auth finished
                                reject(new Error('User canceled the authorization process.'));
                                cleanupAuth();
                                setPending(false);
                            }
                        }
                    }, 1000);
                } catch (error) {
                    reject(error);
                    cleanupAuth();
                }
            });
        },
        [cleanupAuth, authResult],
    );

    const removeAuth = useCallback(async (type: SocialType): Promise<SocialAuthCode> => {
        // disconnect
        // if (type === SocialType.Twitter) {
        //     await getOnePieceServerApi(true).userUnlinkTwitter({ address: user.address });
        //     storage.remove(socialAuthStorageKey(type));
        // }
        return SocialAuthCode.Success;
    }, []);

    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            let key = '';
            if (e.key === socialAuthStorageKey(SocialType.Twitter)) {
                key = SocialType.Twitter;
            } else if (e.key === socialAuthStorageKey(SocialType.Discord)) {
                key = SocialType.Discord;
            } else {
                return;
            }
            try {
                const data = e.newValue ? JSON.parse(e.newValue) : {};
                const code = Number(key === SocialType.Twitter ? data?.twitter_auth : data?.discord_auth);
                const stats = data?.stats ? JSON.parse(atob(data.stats)) : {};
                const authResult: SocialAuthResult = { code, stats };
                console.log(`${key} auth storage changed`, authResult);
                if (authWindowRef.current && !authWindowRef.current.closed) {
                    authWindowRef.current.close();
                }

                setPending(false);
                setAuthResult(authResult);

                if (resolveRef.current) {
                    resolveRef.current(authResult);
                    cleanupAuth();
                }
            } catch (error) {
                if (rejectRef.current) {
                    rejectRef.current(error);
                    cleanupAuth();
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            cleanupAuth();
        };
    }, [cleanupAuth]);

    useEffect(() => {
        return () => {
            cleanupAuth();
            if (authWindowRef.current && !authWindowRef.current.closed) {
                authWindowRef.current.close();
            }
        };
    }, [cleanupAuth]);

    return [auth, removeAuth, pending, authResult];
};

export default useSocialAuth;
